/**
 * Per-archetype special moves (DESIGN §5). Pure data + a tiny pure helper for
 * meter charging; the actual physics effects live in the Bey entity.
 */
import { SIM } from './config.ts';
import type { BeyType } from './stats.ts';

export type SpecialKind = 'rush' | 'counter' | 'secondWind' | 'focus';

export interface SpecialDef {
  kind: SpecialKind;
  name: string;
  /** Short description for the HUD / select screen. */
  blurb: string;
  /** How long the effect lasts, in seconds (0 = instant). */
  duration: number;
}

export const SPECIAL_BY_TYPE: Record<BeyType, SpecialDef> = {
  attack: {
    kind: 'rush',
    name: 'Blazing Rush',
    blurb: 'A homing super-dash that hammers the opponent with massive impact.',
    duration: 0.7,
  },
  defense: {
    kind: 'counter',
    name: 'Iron Counter',
    blurb: 'A guard stance: the next hit is reflected back at the attacker.',
    duration: 1.6,
  },
  stamina: {
    kind: 'secondWind',
    name: 'Second Wind',
    blurb: 'Instantly restores spin and shrugs off drain for a moment.',
    duration: 2.5,
  },
  balance: {
    kind: 'focus',
    name: 'Tailwind',
    blurb: 'A burst of speed and steadier spin, with a small recovery.',
    duration: 3,
  },
};

export const specialFor = (type: BeyType): SpecialDef => SPECIAL_BY_TYPE[type];

/**
 * New meter value after a clash, given spin damage dealt and taken. Clamped to
 * the meter max. (Trickle-over-time is applied separately, per frame.)
 */
export function chargeFromClash(meter: number, dealt: number, taken: number): number {
  const gain = dealt * SIM.SPECIAL.GAIN_PER_DEALT + taken * SIM.SPECIAL.GAIN_PER_TAKEN;
  return Math.min(SIM.SPECIAL.METER_MAX, meter + gain);
}

export const isSpecialReady = (meter: number): boolean => meter >= SIM.SPECIAL.METER_MAX;
